import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, GripVertical, Plus, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * PartnerCardsGridEditor - Éditeur pour le bloc Grille de Cartes Partenaires
 * 
 * Permet de :
 * - Ajouter/supprimer des cartes partenaires
 * - Réorganiser par drag & drop
 * - Upload d'images ou URL
 * - Sélectionner un partenaire existant depuis la base de données
 * - Configurer titre, description, lien
 * - Définir coins arrondis et espacement
 * - Planification par dates (startDate, endDate)
 * 
 * Sécurité :
 * - Select avec value="" pour placeholder (pas de null)
 * - Validation avant création backend
 * - Gestion d'erreurs avec toasts
 */
const PartnerCardsGridEditor = ({ content, onChange, onSaveComplete }) => {
  const { 
    cards = [], 
    corner_radius = 'lg',
    card_spacing = 'md'
  } = content;

  const [visualMode, setVisualMode] = useState('url'); // 'url' ou 'upload'
  const [partners, setPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);

  // Charger la liste des partenaires depuis l'API
  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      setLoadingPartners(true);
      const response = await api.get('/partners?is_active=true');
      // Tri alphabétique A→Z
      const sortedPartners = (response.data || [])
        .slice()
        .sort((a, b) => {
          const nameA = (a.nom || a.name || '').toLowerCase().trim();
          const nameB = (b.nom || b.name || '').toLowerCase().trim();
          return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });
      setPartners(sortedPartners);
    } catch (error) {
      console.error('Erreur chargement partenaires:', error);
    } finally {
      setLoadingPartners(false);
    }
  };

  // Générer slug depuis titre (côté frontend pour preview immédiate)
  const generateSlugFromTitle = (title) => {
    if (!title || !title.trim()) return '';
    
    return title
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')  // Supprimer accents
      .toLowerCase()
      .replace(/[\s_]+/g, '-')          // Espaces → tirets
      .replace(/[^a-z0-9-]/g, '')       // Caractères spéciaux
      .replace(/-+/g, '-')              // Tirets multiples
      .replace(/^-|-$/g, '');           // Tirets début/fin
  };

  // Ajouter une nouvelle carte
  const handleAddCard = () => {
    const newCard = {
      id: `card-${Date.now()}`,
      image: '',
      title: '',
      description: '',
      slug: '',                    // Slug généré
      link: '',                    // Auto-défini comme /partner/[slug]
      partnerId: '',               // Vide par défaut (pas de partenaire sélectionné)
      partnerCardId: null,         // ID de la carte MongoDB (créé lors du save)
      order: cards.length,
      is_active: true,
      startDate: '',               // Date de début optionnelle
      endDate: '',                 // Date de fin optionnelle
      meta: {}                     // Métadonnées supplémentaires
    };

    onChange({
      ...content,
      cards: [...cards, newCard]
    });
  };

  // Supprimer une carte
  const handleDeleteCard = (cardId) => {
    onChange({
      ...content,
      cards: cards.filter(c => c.id !== cardId)
    });
  };

  // Mettre à jour une carte
  const handleUpdateCard = (cardId, field, value) => {
    onChange({
      ...content,
      cards: cards.map(c => {
        if (c.id !== cardId) return c;
        
        const updated = { ...c, [field]: value };
        
        // Si le titre change, regénérer le slug et le link automatiquement
        if (field === 'title') {
          const newSlug = generateSlugFromTitle(value);
          updated.slug = newSlug;
          updated.link = newSlug ? `/partner/${newSlug}` : '';
        }
        
        return updated;
      })
    });
  };

  // Remplir la carte avec les données d'un partenaire sélectionné
  const handleSelectPartner = (cardId, partnerId) => {
    // Si "none", réinitialiser la carte
    if (!partnerId || partnerId === 'none') {
      onChange({
        ...content,
        cards: cards.map(c => {
          if (c.id !== cardId) return c;
          return {
            ...c,
            partnerId: '',
            title: '',
            description: '',
            image: '',
            slug: '',
            link: ''
          };
        })
      });
      return;
    }

    const partner = partners.find(p => p.id === partnerId);
    if (!partner) return;

    const slug = generateSlugFromTitle(partner.nom || partner.name);
    
    onChange({
      ...content,
      cards: cards.map(c => {
        if (c.id !== cardId) return c;
        
        return {
          ...c,
          partnerId: partner.id,
          title: partner.nom || partner.name || '',
          description: partner.description || '',
          image: partner.logo || partner.image_url || c.image,
          slug: slug,
          link: slug ? `/partner/${slug}` : ''
        };
      })
    });
  };

  // Réorganiser les cartes (drag & drop)
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(cards);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Mettre à jour les orders
    const updatedCards = items.map((item, index) => ({
      ...item,
      order: index
    }));

    onChange({
      ...content,
      cards: updatedCards
    });
  };

  // Upload d'image
  const handleImageUpload = async (cardId, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (data.url) {
        handleUpdateCard(cardId, 'image', data.url);
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error('Erreur lors de l\'upload de l\'image');
    }
  };

  // Valider qu'une carte peut être sauvegardée dans le backend
  const validateCardForSave = (card) => {
    const errors = [];
    
    if (!card.title || card.title.trim() === '') {
      errors.push('Le titre est obligatoire');
    }
    
    if (!card.partnerId || card.partnerId === '') {
      errors.push('Sélectionnez un partenaire');
    }
    
    if (!card.slug || card.slug.trim() === '') {
      errors.push('Le slug doit être généré (saisir un titre)');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  };

  // Sauvegarder une carte dans le backend (créer ou mettre à jour)
  const saveCardToBackend = async (card) => {
    // Validation
    const validation = validateCardForSave(card);
    if (!validation.valid) {
      toast.error(`Impossible de sauvegarder la carte : ${validation.errors.join(', ')}`);
      return null;
    }

    try {
      // Si la carte a déjà un partnerCardId, c'est une mise à jour
      if (card.partnerCardId) {
        const response = await api.put(`/partner-cards/${card.partnerCardId}`, {
          title: card.title,
          description: card.description,
          image: card.image,
          partnerId: card.partnerId,
          is_active: card.is_active,
          startDate: card.startDate || null,
          endDate: card.endDate || null,
          order: card.order
        });
        
        return response.data;
      } else {
        // Sinon, créer une nouvelle carte
        const response = await api.post('/partner-cards', {
          title: card.title,
          slug: card.slug,  // Sera normalisé et rendu unique par le backend
          description: card.description,
          image: card.image,
          partnerId: card.partnerId,
          is_active: card.is_active,
          startDate: card.startDate || null,
          endDate: card.endDate || null,
          order: card.order
        });
        
        return response.data;
      }
    } catch (error) {
      console.error('Erreur sauvegarde carte:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur inconnue';
      toast.error(`Erreur sauvegarde carte : ${errorMsg}`);
      return null;
    }
  };

  // Exposer la fonction de sauvegarde via onSaveComplete (appelé depuis le parent)
  useEffect(() => {
    if (onSaveComplete) {
      onSaveComplete(async () => {
        // Sauvegarder toutes les cartes avec partnerId valide
        const results = [];
        const updatedCards = [...cards];
        
        for (let i = 0; i < updatedCards.length; i++) {
          const card = updatedCards[i];
          
          // Si partnerId est vide, c'est une carte en cours de saisie, on la garde telle quelle
          if (!card.partnerId || card.partnerId === '') {
            results.push({ success: true, card });
            continue;
          }
          
          // Sauvegarder la carte dans le backend
          const savedCard = await saveCardToBackend(card);
          
          if (savedCard) {
            // Mettre à jour la carte avec l'ID et le slug final du backend
            updatedCards[i] = {
              ...card,
              partnerCardId: savedCard.id,
              slug: savedCard.slug,
              link: savedCard.link
            };
            results.push({ success: true, card: savedCard });
          } else {
            results.push({ success: false, card });
          }
        }
        
        // Mettre à jour le state avec les cartes mises à jour
        onChange({
          ...content,
          cards: updatedCards
        });
        
        // Retourner le résultat
        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;
        
        if (failCount > 0) {
          toast.warning(`${successCount} carte(s) sauvegardée(s), ${failCount} échec(s)`);
        } else if (successCount > 0) {
          toast.success(`${successCount} carte(s) sauvegardée(s) avec succès`);
        }
        
        return { success: failCount === 0, results };
      });
    }
  }, [cards, content, onChange, onSaveComplete]);

  return (
    <div className="space-y-6">
      {/* Configuration globale */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h4 className="font-semibold text-purple-900 mb-3">Configuration de la Grille Partenaires</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Corner Radius */}
          <div className="space-y-2">
            <Label htmlFor="corner-radius">Coins arrondis</Label>
            <Select
              value={corner_radius}
              onValueChange={(value) => onChange({ ...content, corner_radius: value })}
            >
              <SelectTrigger id="corner-radius">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">Petit (4px)</SelectItem>
                <SelectItem value="md">Moyen (8px)</SelectItem>
                <SelectItem value="lg">Grand (12px)</SelectItem>
                <SelectItem value="xl">Très grand (16px)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Card Spacing */}
          <div className="space-y-2">
            <Label htmlFor="card-spacing">Espacement entre cartes</Label>
            <Select
              value={card_spacing}
              onValueChange={(value) => onChange({ ...content, card_spacing: value })}
            >
              <SelectTrigger id="card-spacing">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">Serré (12px)</SelectItem>
                <SelectItem value="md">Normal (24px)</SelectItem>
                <SelectItem value="lg">Large (32px)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Liste des cartes */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold">Cartes Partenaires ({cards.length})</h4>
          <Button
            type="button"
            size="sm"
            onClick={handleAddCard}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Ajouter une carte
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          📐 Les cartes s'affichent par 3 sur desktop, 1 sur mobile
        </p>

        {cards.length === 0 ? (
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center">
            <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Aucune carte. Cliquez sur "Ajouter" pour commencer.</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="partner-cards">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-3"
                >
                  {cards.map((card, index) => (
                    <Draggable key={card.id} draggableId={card.id} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="bg-white border rounded-lg p-4"
                        >
                          <div className="flex items-start gap-3">
                            {/* Drag Handle */}
                            <div
                              {...provided.dragHandleProps}
                              className="mt-2 text-gray-400 hover:text-gray-600 cursor-grab"
                            >
                              <GripVertical className="h-5 w-5" />
                            </div>

                            {/* Contenu */}
                            <div className="flex-1 space-y-3">
                              {/* Header carte */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-700">
                                    Carte #{index + 1}
                                  </span>
                                  {!card.is_active && (
                                    <Badge variant="secondary" className="text-xs">
                                      Inactive
                                    </Badge>
                                  )}
                                  {card.partnerId && (
                                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300">
                                      Partenaire lié
                                    </Badge>
                                  )}
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteCard(card.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>

                              <div className="space-y-3">
                                {/* Sélection partenaire existant */}
                                {!loadingPartners && partners.length > 0 && (
                                  <div className="space-y-2 bg-purple-50 border border-purple-200 rounded-lg p-3">
                                    <Label className="text-xs font-semibold text-purple-900">
                                      🔗 Remplir depuis un partenaire existant
                                    </Label>
                                    <Select
                                      value={card.partnerId || 'none'}
                                      onValueChange={(value) => handleSelectPartner(card.id, value)}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Sélectionner un partenaire..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">Aucune sélection</SelectItem>
                                        {partners.map((partner) => (
                                          <SelectItem key={partner.id} value={partner.id}>
                                            {partner.nom || partner.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <p className="text-xs text-purple-600">
                                      Sélectionnez un partenaire pour pré-remplir les champs automatiquement
                                    </p>
                                  </div>
                                )}

                                {/* Image */}
                                <div className="space-y-2">
                                  <Label>Image *</Label>
                                  <div className="flex gap-2 mb-2">
                                    <Button
                                      type="button"
                                      variant={visualMode === 'url' ? 'default' : 'outline'}
                                      size="sm"
                                      onClick={() => setVisualMode('url')}
                                    >
                                      URL
                                    </Button>
                                    <Button
                                      type="button"
                                      variant={visualMode === 'upload' ? 'default' : 'outline'}
                                      size="sm"
                                      onClick={() => setVisualMode('upload')}
                                    >
                                      Upload
                                    </Button>
                                  </div>
                                  
                                  {visualMode === 'url' ? (
                                    <Input
                                      type="url"
                                      value={card.image}
                                      onChange={(e) => handleUpdateCard(card.id, 'image', e.target.value)}
                                      placeholder="https://..."
                                    />
                                  ) : (
                                    <Input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => handleImageUpload(card.id, e)}
                                    />
                                  )}

                                  {card.image && (
                                    <img
                                      src={card.image}
                                      alt="Prévisualisation"
                                      className="w-full h-32 object-cover rounded border"
                                    />
                                  )}
                                </div>

                                {/* Titre */}
                                <div className="space-y-2">
                                  <Label>Titre *</Label>
                                  <Input
                                    type="text"
                                    value={card.title}
                                    onChange={(e) => handleUpdateCard(card.id, 'title', e.target.value)}
                                    placeholder="Nom du partenaire"
                                  />
                                </div>

                                {/* Slug (Readonly - Auto-généré depuis titre) */}
                                <div className="space-y-2">
                                  <Label>
                                    Slug (URL)
                                    <span className="text-xs text-muted-foreground ml-2">
                                      • Généré automatiquement
                                    </span>
                                  </Label>
                                  <Input
                                    type="text"
                                    value={card.slug || ''}
                                    readOnly
                                    disabled
                                    className="bg-gray-50 cursor-not-allowed font-mono text-sm"
                                    placeholder="Sera généré depuis le titre..."
                                  />
                                  
                                  {/* Preview URL */}
                                  {card.slug && (
                                    <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                                      <p className="text-xs font-semibold text-purple-900 mb-1">
                                        📎 URL du partenaire :
                                      </p>
                                      <p className="text-xs font-mono text-purple-700 break-all">
                                        {typeof window !== 'undefined' ? window.location.origin : ''}/partner/{card.slug}
                                      </p>
                                    </div>
                                  )}
                                </div>

                                {/* Description */}
                                <div className="space-y-2">
                                  <Label>Description</Label>
                                  <Textarea
                                    value={card.description}
                                    onChange={(e) => handleUpdateCard(card.id, 'description', e.target.value)}
                                    placeholder="Description du partenaire..."
                                    rows={3}
                                  />
                                </div>

                                {/* Active */}
                                <div className="space-y-2">
                                  <Label>Visibilité</Label>
                                  <div className="flex items-center gap-2 pt-1">
                                    <Switch
                                      checked={card.is_active}
                                      onCheckedChange={(checked) => handleUpdateCard(card.id, 'is_active', checked)}
                                    />
                                    <span className="text-sm text-muted-foreground">
                                      {card.is_active ? 'Visible' : 'Cachée'}
                                    </span>
                                  </div>
                                </div>

                                {/* Planification de visibilité (dates) */}
                                {card.is_active && (
                                  <div className="border-t pt-3 mt-3">
                                    <Label className="text-sm font-semibold mb-2 block">
                                      📅 Planification (optionnel)
                                    </Label>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {/* Date de début */}
                                      <div className="space-y-2">
                                        <Label htmlFor={`start-${card.id}`} className="text-xs">
                                          Date de début
                                        </Label>
                                        <Input
                                          id={`start-${card.id}`}
                                          type="datetime-local"
                                          value={card.startDate || ''}
                                          onChange={(e) => handleUpdateCard(card.id, 'startDate', e.target.value)}
                                          className="text-sm"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                          {card.startDate 
                                            ? `Visible à partir du ${new Date(card.startDate).toLocaleString('fr-FR')}`
                                            : 'Visible immédiatement'
                                          }
                                        </p>
                                      </div>

                                      {/* Date de fin */}
                                      <div className="space-y-2">
                                        <Label htmlFor={`end-${card.id}`} className="text-xs">
                                          Date de fin
                                        </Label>
                                        <Input
                                          id={`end-${card.id}`}
                                          type="datetime-local"
                                          value={card.endDate || ''}
                                          onChange={(e) => handleUpdateCard(card.id, 'endDate', e.target.value)}
                                          className="text-sm"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                          {card.endDate 
                                            ? `Visible jusqu'au ${new Date(card.endDate).toLocaleString('fr-FR')}`
                                            : 'Visible indéfiniment'
                                          }
                                        </p>
                                      </div>
                                    </div>

                                    {/* Validation endDate >= startDate */}
                                    {card.startDate && card.endDate && new Date(card.endDate) < new Date(card.startDate) && (
                                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                        ⚠️ La date de fin doit être postérieure à la date de début
                                      </div>
                                    )}

                                    {/* Indicateur visuel de programmation */}
                                    {(card.startDate || card.endDate) && (
                                      <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-700">
                                        ✓ Carte programmée
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Aide et Avertissements */}
      <div className="space-y-3">
        {/* Avertissement si cartes sans partenaire */}
        {cards.some(c => !c.partnerId || c.partnerId === '') && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-yellow-900 mb-1">
                ⚠️ Cartes incomplètes
              </p>
              <p className="text-yellow-700">
                Certaines cartes n'ont pas de partenaire sélectionné. 
                <strong> Sélectionnez un partenaire pour chaque carte</strong> avant de sauvegarder le bloc, 
                sinon elles ne seront pas créées dans la base de données.
              </p>
            </div>
          </div>
        )}

        {/* Conseils */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
          <p className="font-semibold mb-2">💡 Conseils :</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Images recommandées : 800x600px minimum (ratio 4:3)</li>
            <li>Les cartes s'affichent par 3 sur desktop, 1 sur mobile</li>
            <li>Utilisez le drag & drop pour réorganiser les cartes</li>
            <li>Les cartes inactives ne seront pas affichées</li>
            <li><strong>Important :</strong> Sélectionnez un partenaire pour chaque carte avant sauvegarde</li>
            <li>Le slug (URL) est généré automatiquement depuis le titre</li>
            <li>Les dates s'affichent automatiquement si définies (sans heure)</li>
            <li>Utilisez la planification pour afficher temporairement une carte partenaire</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PartnerCardsGridEditor;
